import { num } from './utilities.js'

// RED-235: a cell that shows the dual "suletud netopind / eluruumi pind" area
// keeps the eluruumi (dwelling) value under its column id, and carries the SNP
// value under `<columnId>__snp`. Both stay separate so each can be its own
// editable input (Toomas, RED-235 Q6 — "Ka need peavad olema eraldi
// töödeldavad, kuna Kasutajal võib tekkida vajadus andmeid korrigeerida"). The
// two values are only collapsed into one "snp / dwelling" string at export time.
export const DUAL_AREA_SNP_SUFFIX = '__snp'

/**
 * Format two areas for the RED-235 dual "suletud netopind / eluruumi pind"
 * cell, degrading gracefully when one value is missing.
 *
 * A value counts as present only when it is a positive number — this also
 * treats the string "0" (the cached subject SNP default from
 * EhrBuildingDataService::resolveTechnical) as missing, so an üksikelamu with no
 * real SNP never renders a dangling "0 / 120".
 *
 * - both present  -> "snp / dwelling"
 * - snp only      -> "snp"            (EHR leaves the dwelling area empty for ~93% of houses)
 * - dwelling only -> "dwelling"       (preserves the pre-RED-235 single-value behaviour)
 * - neither       -> ""               (rendered as a blank cell)
 *
 * Areas are rounded to one decimal for a clean, consistent dual display.
 *
 * @param {number|string|null|undefined} snp closed net area (suletud netopind)
 * @param {number|string|null|undefined} dwelling dwelling/unit area (hooneosa_pind)
 * @returns {string}
 */
export const formatDualArea = (snp, dwelling) => {
  const present = v => v !== null && v !== undefined && v !== '' && num(v) > 0
  const fmt = v => String(Math.round(num(v) * 10) / 10)

  if (present(snp) && present(dwelling)) {
    return `${fmt(snp)} / ${fmt(dwelling)}`
  }
  if (present(snp)) {
    return fmt(snp)
  }
  if (present(dwelling)) {
    return fmt(dwelling)
  }

  return ''
}

/**
 * Mark the dual-area columns on a RED-235 comparison- or selection-table area
 * row, in place.
 *
 * Only the columns whose own building is an üksikelamu (`isHouse`) become dual
 * cells; every other otstarve keeps its raw single dwelling value untouched, so
 * a non-üksikelamu column never shows the building-SNP / unit-area pairing. For
 * each üksikelamu column we stash the SNP value under `<id>__snp` (the dwelling
 * value already lives under `<id>`) and record the column id in `row.dualCols`,
 * which the table component reads to render two separate editable inputs.
 *
 * The caller MUST have already set the raw per-column values on `row`.
 *
 * @param {object} row mutated in place
 * @param {Array<{id: string|number, snp: *, isHouse: boolean}>} columns
 * @returns {boolean} anyHouse — true when at least one column is a dual cell
 *   (the caller uses this to apply the "(suletud netopind / eluruumi pind)"
 *   label clarifier)
 */
export const applyDualAreaColumns = (row, columns) => {
  const dualCols = []

  for (const { id, snp, isHouse } of columns || []) {
    if (!isHouse) {
      continue
    }
    const key = id + ''
    row[key + DUAL_AREA_SNP_SUFFIX] = snp ?? null
    dualCols.push(key)
  }

  if (dualCols.length > 0) {
    row.dualCols = dualCols
  }

  return dualCols.length > 0
}

/**
 * Collapse the per-column dual-area values back into single "snp / dwelling"
 * strings for export (Word/PDF via DocxService, Excel via /export-excel), which
 * replay one string per cell. On-screen the two values stay separate so each is
 * editable; this is the single point where they are joined for output.
 *
 * Returns a shallow-cloned row list; rows without `dualCols` pass through
 * untouched.
 *
 * @param {Array<object>} rows
 * @returns {Array<object>}
 */
export const collapseDualAreaForExport = rows =>
  (rows || []).map(row => {
    if (!row?.dualCols || row.dualCols.length === 0) {
      return row
    }

    const out = { ...row }
    for (const colId of row.dualCols) {
      out[colId] = formatDualArea(row[colId + DUAL_AREA_SNP_SUFFIX], row[colId])
      delete out[colId + DUAL_AREA_SNP_SUFFIX]
    }
    delete out.dualCols

    return out
  })
